import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaExclamationTriangle, FaUndo, FaSignOutAlt, FaCalendarAlt } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config';
import useDeletionStatus from '../../hooks/useDeletionStatus';
import deletionStatusService from '../../services/deletionStatusService';

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 24px;
  text-align: center;
`;

const WarningIcon = styled.div`
  font-size: 64px;
  color: #dc3545;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const Description = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const InfoBox = styled.div`
  background: ${props => props.theme === 'dark' ? '#2a2a2a' : '#ffffff'};
  border: 1px solid ${props => props.theme === 'dark' ? '#404040' : '#e0e0e0'};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  text-align: left;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #FF6B00;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
`;

const InfoText = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  color: var(--text-secondary);
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }
  ` : props.variant === 'secondary' ? `
    background: transparent;
    color: var(--text-secondary);
    border: 1px solid ${props.theme === 'dark' ? '#404040' : '#ddd'};
    
    &:hover {
      background: ${props.theme === 'dark' ? '#404040' : '#f5f5f5'};
    }
  ` : `
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #5a6268;
      transform: translateY(-2px);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const CountdownBox = styled.div`
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 32px;
`;

const CountdownTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const CountdownValue = styled.div`
  font-size: 24px;
  font-weight: 700;
`;

const AccountDeletionPending = () => {
  const { theme } = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { status: deletionInfo, loading } = useDeletionStatus();
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    // Якщо статус завантажено і видалення не запитано, перенаправляємо
    if (!loading && !deletionInfo?.deletionRequested) {
      navigate('/');
    }
  }, [deletionInfo, loading, navigate]);

  const handleCancelDeletion = async () => {
    if (!window.confirm(
      'Ви впевнені, що хочете скасувати запит на видалення акаунту? ' +
      'Ваш акаунт залишиться активним.'
    )) {
      return;
    }

    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.apiUrl}/api/gdpr/cancel-deletion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Очищуємо кеш після скасування
      deletionStatusService.clearCache();
      alert('Запит на видалення акаунту скасовано! Ваш акаунт знову активний.');
      navigate('/');
    } catch (error) {
      console.error('Error cancelling deletion:', error);
      alert('Помилка при скасуванні запиту на видалення');
    } finally {
      setCancelling(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const calculateDaysLeft = () => {
    if (!deletionInfo?.scheduledDeletion) return 0;
    
    const now = new Date();
    const deletionDate = new Date(deletionInfo.scheduledDeletion);
    const diffTime = deletionDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container>
        <div>Завантаження...</div>
      </Container>
    );
  }

  if (!deletionInfo?.deletionRequested) {
    navigate('/');
    return null;
  }

  const daysLeft = calculateDaysLeft();

  return (
    <Container>
      <WarningIcon>
        <FaExclamationTriangle />
      </WarningIcon>
      
      <Title>Акаунт заплановано до видалення</Title>
      
      <Description>
        Ви запросили видалення свого акаунту. Всі ваші дані будуть повністю видалені 
        через зазначений час. До цього моменту ви можете скасувати запит.
      </Description>

      <CountdownBox>
        <CountdownTitle>Залишилось часу:</CountdownTitle>
        <CountdownValue>
          {daysLeft} {daysLeft === 1 ? 'день' : daysLeft < 5 ? 'дні' : 'днів'}
        </CountdownValue>
      </CountdownBox>

      <InfoBox theme={theme}>
        <InfoItem>
          <InfoIcon>
            <FaCalendarAlt />
          </InfoIcon>
          <InfoText>
            <InfoLabel>Дата запиту</InfoLabel>
            <InfoValue>{formatDate(deletionInfo.deletionRequestedAt)}</InfoValue>
          </InfoText>
        </InfoItem>
        
        <InfoItem>
          <InfoIcon>
            <FaCalendarAlt />
          </InfoIcon>
          <InfoText>
            <InfoLabel>Планове видалення</InfoLabel>
            <InfoValue>{formatDate(deletionInfo.scheduledDeletion)}</InfoValue>
          </InfoText>
        </InfoItem>
      </InfoBox>

      <ButtonGroup>
        <Button 
          variant="primary" 
          onClick={handleCancelDeletion}
          disabled={cancelling}
        >
          <FaUndo />
          {cancelling ? 'Скасування...' : 'Скасувати видалення'}
        </Button>
        
        <Button onClick={handleLogout}>
          <FaSignOutAlt />
          Вийти з акаунту
        </Button>
      </ButtonGroup>
    </Container>
  );
};

export default AccountDeletionPending;